import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import {
  Search, Users, Calendar, Heart, ChevronRight, X, Phone, Mail,
  MessageSquare, Video, ClipboardList, MapPin, Clock, Shield,
  UserPlus, FileText, Activity, ChevronDown, ChevronUp, ArrowUpDown, Check, Plus, Repeat
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminClients() {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [showAssign, setShowAssign] = useState(false);
  const [caregivers, setCaregivers] = useState([]);
  const [assignForm, setAssignForm] = useState({ caregiver_id: '', date: '', start_time: '09:00', end_time: '17:00', notes: '', recurring: false, recurrence_pattern: 'weekly', recurrence_end: '' });
  const [assigning, setAssigning] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { fetchClients(); }, []);

  const fetchClients = async (q = '') => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/clients${q ? `?search=${encodeURIComponent(q)}` : ''}`, { withCredentials: true });
      setClients(data);
    } catch { toast.error('Failed to load clients'); }
    setLoading(false);
  };

  const fetchDetail = async (id) => {
    setDetailLoading(true);
    setSelected(id);
    setShowAssign(false);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/clients/${id}`, { withCredentials: true });
      setDetail(data);
    } catch { toast.error('Failed to load details'); }
    setDetailLoading(false);
  };

  const fetchCaregivers = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/caregivers`, { withCredentials: true });
      setCaregivers(data);
    } catch { toast.error('Failed to load caregivers'); }
  };

  const openAssign = () => {
    fetchCaregivers();
    setAssignForm({ caregiver_id: '', date: '', start_time: '09:00', end_time: '17:00', notes: '', recurring: false, recurrence_pattern: 'weekly', recurrence_end: '' });
    setShowAssign(true);
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    if (!assignForm.caregiver_id || !assignForm.date) {
      toast.error('Select a caregiver and date');
      return;
    }
    if (assignForm.recurring && !assignForm.recurrence_end) {
      toast.error('Set an end date for recurring shifts');
      return;
    }
    setAssigning(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/clients/${selected}/assign-caregiver`, assignForm, { withCredentials: true });
      if (data.conflicts?.length > 0) {
        toast.warning(`${data.shifts_created} shift(s) created — ${data.conflicts.length} conflict(s) detected`, { duration: 5000 });
      } else {
        toast.success(`${data.shifts_created} shift(s) created for ${data.caregiver_name}`);
      }
      setShowAssign(false);
      fetchDetail(selected);
      fetchClients();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Assignment failed');
    }
    setAssigning(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchClients(search);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sorted = [...clients].sort((a, b) => {
    const av = a[sortField] ?? '';
    const bv = b[sortField] ?? '';
    if (typeof av === 'number') return sortDir === 'asc' ? av - bv : bv - av;
    return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
  });

  const filtered = search.trim()
    ? sorted.filter(c =>
        c.name?.toLowerCase().includes(search.toLowerCase()) ||
        c.email?.toLowerCase().includes(search.toLowerCase()) ||
        c.usic?.toLowerCase().includes(search.toLowerCase())
      )
    : sorted;

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 text-muted-foreground/40" />;
    return sortDir === 'asc' ? <ChevronUp className="w-3 h-3 text-[#00A6A0]" /> : <ChevronDown className="w-3 h-3 text-[#00A6A0]" />;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-clients-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">Clients</h1>
            <p className="text-muted-foreground text-sm mt-1">{clients.length} client(s) in your agency</p>
          </div>
          <div className="flex gap-2">
            <form onSubmit={handleSearch} className="flex gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search name, email, USIC..."
                  className="pl-9 bg-muted border-border text-foreground w-56"
                  data-testid="client-search"
                />
              </div>
              <Button type="submit" variant="outline" className="border-border text-foreground" data-testid="client-search-btn">Search</Button>
            </form>
            <Button className="btn-primary gap-2" onClick={() => navigate('/bulk-invite')} data-testid="invite-client-btn">
              <UserPlus className="w-4 h-4" /> Invite Client
            </Button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className="glass-card border-border"><CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-[#00A6A0]">{clients.length}</p>
            <p className="text-xs text-muted-foreground">Total Clients</p>
          </CardContent></Card>
          <Card className="glass-card border-border"><CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-[#A8E6CF]">{clients.filter(c => c.status === 'active').length}</p>
            <p className="text-xs text-muted-foreground">Active</p>
          </CardContent></Card>
          <Card className="glass-card border-border"><CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-amber-400">{clients.reduce((s, c) => s + (c.upcoming_shifts || 0), 0)}</p>
            <p className="text-xs text-muted-foreground">Upcoming Shifts</p>
          </CardContent></Card>
          <Card className="glass-card border-border"><CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-[#FF6F61]">{clients.reduce((s, c) => s + (c.assigned_caregivers || 0), 0)}</p>
            <p className="text-xs text-muted-foreground">Caregiver Assignments</p>
          </CardContent></Card>
        </div>

        <div className="flex gap-6">
          {/* Client Table */}
          <div className={`${selected ? 'hidden lg:block lg:w-[45%]' : 'w-full'} space-y-0`}>
            {loading ? (
              <div className="flex justify-center py-12"><div className="spinner w-8 h-8" /></div>
            ) : clients.length === 0 ? (
              <div className="text-center py-12 glass-card rounded-xl border border-border">
                <Heart className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
                <p className="text-foreground font-medium mb-1">No clients found</p>
                <p className="text-xs text-muted-foreground">Invite clients to your agency to see them here</p>
                <Button className="btn-primary mt-4 gap-2" onClick={() => navigate('/bulk-invite')}>
                  <UserPlus className="w-4 h-4" /> Invite Client
                </Button>
              </div>
            ) : (
              <Card className="glass-card border-border overflow-hidden">
                {/* Table Header */}
                <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-muted/50 border-b border-border text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  <button onClick={() => handleSort('name')} className="col-span-4 flex items-center gap-1 hover:text-foreground transition-colors text-left" data-testid="sort-name">
                    Client <SortIcon field="name" />
                  </button>
                  <button onClick={() => handleSort('status')} className="col-span-2 flex items-center gap-1 hover:text-foreground transition-colors" data-testid="sort-status">
                    Status <SortIcon field="status" />
                  </button>
                  <button onClick={() => handleSort('assigned_caregivers')} className="col-span-2 flex items-center gap-1 hover:text-foreground transition-colors" data-testid="sort-caregivers">
                    Caregivers <SortIcon field="assigned_caregivers" />
                  </button>
                  <button onClick={() => handleSort('upcoming_shifts')} className="col-span-2 flex items-center gap-1 hover:text-foreground transition-colors" data-testid="sort-shifts">
                    Shifts <SortIcon field="upcoming_shifts" />
                  </button>
                  <button onClick={() => handleSort('care_plans')} className="col-span-2 flex items-center gap-1 hover:text-foreground transition-colors" data-testid="sort-plans">
                    Plans <SortIcon field="care_plans" />
                  </button>
                </div>

                {/* Table Rows */}
                <div className="divide-y divide-border max-h-[calc(100vh-380px)] overflow-y-auto">
                  {filtered.map((cl) => (
                    <div
                      key={cl.id}
                      onClick={() => fetchDetail(cl.id)}
                      className={`grid grid-cols-12 gap-2 px-4 py-3 cursor-pointer transition-all duration-150 items-center ${
                        selected === cl.id
                          ? 'bg-[#00A6A0]/5 border-l-2 border-l-[#00A6A0]'
                          : 'hover:bg-muted/30'
                      }`}
                      data-testid={`client-row-${cl.id}`}
                    >
                      {/* Name + Email */}
                      <div className="col-span-4 flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-full bg-[#A8E6CF]/10 flex items-center justify-center flex-shrink-0">
                          <span className="text-[#A8E6CF] font-bold text-sm">{cl.name?.charAt(0)?.toUpperCase()}</span>
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm text-foreground font-medium truncate">{cl.name}</p>
                          <p className="text-[11px] text-muted-foreground truncate">{cl.email}</p>
                        </div>
                      </div>

                      {/* Status */}
                      <div className="col-span-2">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium ${
                          cl.status === 'active'
                            ? 'bg-[#A8E6CF]/15 text-[#A8E6CF]'
                            : 'bg-red-500/15 text-red-400'
                        }`}>
                          {cl.status}
                        </span>
                      </div>

                      {/* Caregivers */}
                      <div className="col-span-2">
                        <div className="flex items-center gap-1.5">
                          <Users className="w-3.5 h-3.5 text-muted-foreground" />
                          <span className="text-sm text-foreground">{cl.assigned_caregivers}</span>
                        </div>
                      </div>

                      {/* Upcoming Shifts */}
                      <div className="col-span-2">
                        <div className="flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
                          <span className="text-sm text-foreground">{cl.upcoming_shifts}</span>
                        </div>
                      </div>

                      {/* Care Plans */}
                      <div className="col-span-2 flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <ClipboardList className="w-3.5 h-3.5 text-muted-foreground" />
                          <span className="text-sm text-foreground">{cl.care_plans}</span>
                        </div>
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Table Footer */}
                <div className="px-4 py-2.5 border-t border-border bg-muted/30 text-xs text-muted-foreground">
                  Showing {filtered.length} of {clients.length} client(s)
                </div>
              </Card>
            )}
          </div>

          {/* Detail Panel */}
          {selected && (
            <div className="flex-1 min-w-0 space-y-4" data-testid="client-detail-panel">
              {detailLoading ? (
                <div className="flex justify-center py-12"><div className="spinner w-8 h-8" /></div>
              ) : detail ? (
                <>
                  {/* Client Header Card */}
                  <Card className="glass-card border-border">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-14 rounded-full bg-[#A8E6CF]/10 flex items-center justify-center">
                            <span className="text-[#A8E6CF] font-bold text-xl">{detail.name?.charAt(0)?.toUpperCase()}</span>
                          </div>
                          <div>
                            <h2 className="text-xl font-heading font-bold text-foreground">{detail.name}</h2>
                            <p className="text-sm text-muted-foreground">{detail.usic}</p>
                            <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${
                              detail.status === 'active' ? 'bg-[#A8E6CF]/15 text-[#A8E6CF]' : 'bg-red-500/15 text-red-400'
                            }`}>{detail.status}</span>
                          </div>
                        </div>
                        <Button variant="ghost" size="icon" onClick={() => { setSelected(null); setDetail(null); }} data-testid="close-detail-btn">
                          <X className="w-5 h-5" />
                        </Button>
                      </div>

                      {/* Contact Info */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm mb-4">
                        <div className="flex items-center gap-2 text-muted-foreground"><Mail className="w-4 h-4 text-[#00A6A0]" />{detail.email}</div>
                        <div className="flex items-center gap-2 text-muted-foreground"><Phone className="w-4 h-4 text-[#00A6A0]" />{detail.phone || 'Not set'}</div>
                      </div>

                      {/* Quick Actions */}
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" className="btn-primary gap-2" onClick={() => navigate('/messaging')} data-testid="action-message">
                          <MessageSquare className="w-3.5 h-3.5" /> Message
                        </Button>
                        <Button size="sm" variant="outline" className="border-border text-foreground gap-2" onClick={() => navigate('/video-call')} data-testid="action-video">
                          <Video className="w-3.5 h-3.5" /> Video Call
                        </Button>
                        <Button size="sm" variant="outline" className="border-border text-foreground gap-2" onClick={() => navigate('/dashboard/admin/shifts')} data-testid="action-shifts">
                          <Calendar className="w-3.5 h-3.5" /> Manage Shifts
                        </Button>
                        <Button size="sm" variant="outline" className="border-border text-foreground gap-2" onClick={() => navigate('/care-journals')} data-testid="action-care">
                          <FileText className="w-3.5 h-3.5" /> Care Journals
                        </Button>
                        <Button size="sm" variant="outline" className="border-border text-foreground gap-2" onClick={() => navigate(`/client-folders`)} data-testid="action-folders">
                          <Shield className="w-3.5 h-3.5" /> Documents
                        </Button>
                        <Button size="sm" className="bg-[#00A6A0] hover:bg-[#009590] text-white gap-2" onClick={() => navigate(`/client-record/${selected}`)} data-testid="action-dscr">
                          <FileText className="w-3.5 h-3.5" /> Full DSCR Record
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Assigned Caregivers */}
                  <Card className="glass-card border-border">
                    <CardHeader className="pb-2 border-b border-border">
                      <CardTitle className="text-sm font-heading text-foreground flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-[#FF6F61]" />
                          Assigned Caregivers ({detail.assigned_caregivers?.length || 0})
                        </span>
                        <Button size="sm" variant="outline" className="h-7 text-xs gap-1 border-[#00A6A0]/30 text-[#00A6A0] hover:bg-[#00A6A0]/10" onClick={openAssign} data-testid="open-assign-btn">
                          <Plus className="w-3 h-3" /> Assign
                        </Button>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-3">
                      {/* Inline Assign Form */}
                      {showAssign && (
                        <form onSubmit={handleAssign} className="mb-3 p-3 rounded-lg border border-[#00A6A0]/20 bg-[#00A6A0]/5 space-y-3" data-testid="assign-form">
                          <p className="text-xs font-medium text-foreground">Assign Caregiver to Shift</p>
                          <div>
                            <label className="text-[11px] text-muted-foreground block mb-1">Caregiver</label>
                            <Select value={assignForm.caregiver_id} onValueChange={(v) => setAssignForm(f => ({...f, caregiver_id: v}))}>
                              <SelectTrigger className="bg-muted border-border text-foreground h-8 text-xs" data-testid="assign-cg-select">
                                <SelectValue placeholder="Select caregiver..." />
                              </SelectTrigger>
                              <SelectContent>
                                {caregivers.map(cg => (
                                  <SelectItem key={cg.id} value={cg.id} data-testid={`cg-option-${cg.id}`}>
                                    {cg.name} ({cg.usic})
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="grid grid-cols-3 gap-2">
                            <div>
                              <label className="text-[11px] text-muted-foreground block mb-1">Date</label>
                              <Input type="date" value={assignForm.date} onChange={(e) => setAssignForm(f => ({...f, date: e.target.value}))} className="bg-muted border-border text-foreground h-8 text-xs" data-testid="assign-date" />
                            </div>
                            <div>
                              <label className="text-[11px] text-muted-foreground block mb-1">Start</label>
                              <Input type="time" value={assignForm.start_time} onChange={(e) => setAssignForm(f => ({...f, start_time: e.target.value}))} className="bg-muted border-border text-foreground h-8 text-xs" data-testid="assign-start" />
                            </div>
                            <div>
                              <label className="text-[11px] text-muted-foreground block mb-1">End</label>
                              <Input type="time" value={assignForm.end_time} onChange={(e) => setAssignForm(f => ({...f, end_time: e.target.value}))} className="bg-muted border-border text-foreground h-8 text-xs" data-testid="assign-end" />
                            </div>
                          </div>
                          <div>
                            <label className="text-[11px] text-muted-foreground block mb-1">Notes (optional)</label>
                            <Input value={assignForm.notes} onChange={(e) => setAssignForm(f => ({...f, notes: e.target.value}))} placeholder="Shift notes..." className="bg-muted border-border text-foreground h-8 text-xs" data-testid="assign-notes" />
                          </div>

                          {/* Recurring Toggle */}
                          <div className="space-y-2">
                            <label className="flex items-center gap-2 cursor-pointer" data-testid="recurring-toggle">
                              <div
                                onClick={() => setAssignForm(f => ({...f, recurring: !f.recurring}))}
                                className={`w-8 h-4.5 rounded-full relative transition-colors duration-200 ${assignForm.recurring ? 'bg-[#00A6A0]' : 'bg-muted-foreground/30'}`}
                                style={{ padding: '2px', width: '32px', height: '18px' }}
                              >
                                <div className={`w-3.5 h-3.5 rounded-full bg-white shadow transition-transform duration-200 ${assignForm.recurring ? 'translate-x-3.5' : 'translate-x-0'}`} style={{ width: '14px', height: '14px' }} />
                              </div>
                              <span className="text-xs text-foreground flex items-center gap-1">
                                <Repeat className="w-3 h-3" /> Recurring Assignment
                              </span>
                            </label>

                            {assignForm.recurring && (
                              <div className="grid grid-cols-2 gap-2 pl-10">
                                <div>
                                  <label className="text-[11px] text-muted-foreground block mb-1">Pattern</label>
                                  <Select value={assignForm.recurrence_pattern} onValueChange={(v) => setAssignForm(f => ({...f, recurrence_pattern: v}))}>
                                    <SelectTrigger className="bg-muted border-border text-foreground h-8 text-xs" data-testid="recurring-pattern">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="weekly">Weekly</SelectItem>
                                      <SelectItem value="biweekly">Biweekly</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <label className="text-[11px] text-muted-foreground block mb-1">Until</label>
                                  <Input type="date" value={assignForm.recurrence_end} onChange={(e) => setAssignForm(f => ({...f, recurrence_end: e.target.value}))} className="bg-muted border-border text-foreground h-8 text-xs" data-testid="recurring-end" />
                                </div>
                                {assignForm.date && assignForm.recurrence_end && (
                                  <div className="col-span-2">
                                    <p className="text-[10px] text-[#00A6A0]" data-testid="recurring-preview">
                                      {(() => {
                                        const start = new Date(assignForm.date);
                                        const end = new Date(assignForm.recurrence_end);
                                        if (isNaN(start) || isNaN(end) || end <= start) return '';
                                        const step = assignForm.recurrence_pattern === 'weekly' ? 7 : 14;
                                        let count = 1;
                                        let cur = new Date(start);
                                        cur.setDate(cur.getDate() + step);
                                        while (cur <= end) { count++; cur.setDate(cur.getDate() + step); }
                                        return `${count} shift(s) will be created (${assignForm.recurrence_pattern}, ${assignForm.date} to ${assignForm.recurrence_end})`;
                                      })()}
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>

                          <div className="flex gap-2">
                            <Button type="submit" size="sm" className="btn-primary gap-1 text-xs h-7" disabled={assigning} data-testid="assign-submit-btn">
                              <Check className="w-3 h-3" /> {assigning ? 'Assigning...' : 'Confirm Assignment'}
                            </Button>
                            <Button type="button" size="sm" variant="ghost" className="text-xs h-7" onClick={() => setShowAssign(false)} data-testid="assign-cancel-btn">Cancel</Button>
                          </div>
                        </form>
                      )}

                      {!detail.assigned_caregivers?.length && !showAssign ? (
                        <div className="py-4 text-center">
                          <p className="text-sm text-muted-foreground">No caregivers assigned yet</p>
                          <p className="text-xs text-muted-foreground mt-1">Click "Assign" above to add one</p>
                        </div>
                      ) : null}
                      {detail.assigned_caregivers?.length > 0 && (
                        <div className="space-y-2">
                          {detail.assigned_caregivers.map((cg) => (
                            <div key={cg.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors" data-testid={`cg-${cg.id}`}>
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-[#FF6F61]/10 flex items-center justify-center flex-shrink-0">
                                  <span className="text-[#FF6F61] font-bold text-xs">{cg.name?.charAt(0)?.toUpperCase()}</span>
                                </div>
                                <div>
                                  <p className="text-foreground font-medium text-sm">{cg.name}</p>
                                  <p className="text-[11px] text-muted-foreground">{cg.staff_number || cg.usic}</p>
                                </div>
                              </div>
                              <div className="flex gap-1">
                                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); navigate('/messaging'); }} title="Message">
                                  <MessageSquare className="w-3.5 h-3.5 text-muted-foreground" />
                                </Button>
                                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/admin/caregivers`); }} title="View Profile">
                                  <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Care Plans */}
                  <Card className="glass-card border-border">
                    <CardHeader className="pb-2 border-b border-border">
                      <CardTitle className="text-sm font-heading text-foreground flex items-center gap-2">
                        <ClipboardList className="w-4 h-4 text-[#00A6A0]" />
                        Care Plans ({detail.care_plans?.length || 0})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-3">
                      {!detail.care_plans?.length ? (
                        <div className="py-4 text-center">
                          <p className="text-sm text-muted-foreground">No care plans created</p>
                          <Button size="sm" variant="outline" className="mt-2 gap-2 border-border text-foreground" onClick={() => navigate('/care-journals')} data-testid="create-plan-btn">
                            <FileText className="w-3.5 h-3.5" /> View Care Journals
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {detail.care_plans.map((p, i) => (
                            <div key={i} className="p-3 rounded-lg bg-muted/50">
                              <p className="text-foreground font-medium text-sm">{p.title || 'Care Plan'}</p>
                              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{p.description || 'No description'}</p>
                              {p.category && <span className="inline-block mt-1.5 px-2 py-0.5 rounded-full text-[10px] bg-[#00A6A0]/10 text-[#00A6A0]">{p.category}</span>}
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Shift History & Patterns */}
                  <Card className="glass-card border-border">
                    <CardHeader className="pb-2 border-b border-border">
                      <CardTitle className="text-sm font-heading text-foreground flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-amber-400" />
                        Shift History
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-3">
                      {!detail.recent_shifts?.length ? (
                        <div className="py-4 text-center">
                          <p className="text-sm text-muted-foreground">No shifts recorded</p>
                          <Button size="sm" variant="outline" className="mt-2 gap-2 border-border text-foreground" onClick={() => navigate('/dashboard/admin/shifts')} data-testid="create-shift-btn">
                            <Calendar className="w-3.5 h-3.5" /> Create Shift
                          </Button>
                        </div>
                      ) : (
                        <>
                          {/* Shift Pattern Summary */}
                          <div className="grid grid-cols-3 gap-2 mb-3">
                            <div className="p-2 rounded-lg bg-[#A8E6CF]/5 border border-[#A8E6CF]/20 text-center">
                              <p className="text-lg font-bold text-[#A8E6CF]">{detail.recent_shifts.filter(s => s.status === 'completed').length}</p>
                              <p className="text-[10px] text-muted-foreground">Completed</p>
                            </div>
                            <div className="p-2 rounded-lg bg-blue-400/5 border border-blue-400/20 text-center">
                              <p className="text-lg font-bold text-blue-400">{detail.recent_shifts.filter(s => ['assigned', 'open'].includes(s.status)).length}</p>
                              <p className="text-[10px] text-muted-foreground">Upcoming</p>
                            </div>
                            <div className="p-2 rounded-lg bg-amber-400/5 border border-amber-400/20 text-center">
                              <p className="text-lg font-bold text-amber-400">{detail.recent_shifts.filter(s => s.status === 'in_progress').length}</p>
                              <p className="text-[10px] text-muted-foreground">In Progress</p>
                            </div>
                          </div>

                          {/* Shift List */}
                          <div className="space-y-1.5 max-h-64 overflow-y-auto">
                            {detail.recent_shifts.map((s, i) => (
                              <div key={i} className="flex items-center justify-between text-xs p-2.5 rounded-lg bg-muted/50" data-testid={`shift-row-${i}`}>
                                <div className="flex items-center gap-2 min-w-0">
                                  <Clock className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                                  <div className="min-w-0">
                                    <p className="text-foreground font-medium truncate">{s.date || 'N/A'}</p>
                                    <p className="text-muted-foreground">{s.start_time} - {s.end_time}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  {s.caregiver_name && <span className="text-muted-foreground truncate max-w-[100px]">{s.caregiver_name}</span>}
                                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                    s.status === 'completed' ? 'bg-[#A8E6CF]/15 text-[#A8E6CF]' :
                                    s.status === 'in_progress' ? 'bg-amber-400/15 text-amber-400' :
                                    s.status === 'assigned' ? 'bg-blue-400/15 text-blue-400' :
                                    'bg-muted text-muted-foreground'
                                  }`}>{s.status}</span>
                                </div>
                              </div>
                            ))}
                          </div>

                          <Button size="sm" variant="outline" className="w-full mt-3 border-border text-foreground gap-2" onClick={() => navigate('/dashboard/admin/shifts')} data-testid="view-all-shifts-btn">
                            <Calendar className="w-3.5 h-3.5" /> View All Shifts
                          </Button>
                        </>
                      )}
                    </CardContent>
                  </Card>
                </>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
